import i18next from 'i18next';
import { InternalResourceStatusEnum, ResourceStatusEnum } from '../../Protocol/IHttpInterface';
import {
    ConsentOtpStatusEnum,
    IDocumentType,
    ISubmitRegistrationRequest,
    ISubmitRegistrationResponseData,
    IValidateCustomerIdResponseData,
    IValidateReverseOtpRequest,
    IIdentityDocument
} from '../../Service/IRegistrationService';

import Constants, {
    DOCUMENT_TYPES,
    ISOLanguageCodeEnum,
    LOGGER_TAGS,
    OTP_OPERATION_TYPE
} from '../../Utils/Constants';
import { ERROR_CODES } from '../../Utils/Constants/Errors';
import Logger from '../../Utils/Logger';
import Utils from '../../Utils/Utils';
import RegistrationManager from './RegistrationManager';
import { Occupations as PROD_OCCUPATIONS } from '../../Utils/Constants/occupations.prod';
import { Occupations as UAT_OCCUPATIONS } from '../../Utils/Constants/occupations.uat';

class CustomerRegistrationManager extends RegistrationManager {
    private _customerIRegistrationId!: string;

    private _whiteListedResponse: string = '';

    private _setDocumentType?: (docType: IDocumentType) => void;

    public setDocumentTypeCallback(callback: (docType: IDocumentType) => void) {
        this._setDocumentType = callback;
    }

    createAccount(
        isMetadatForm: boolean,
        isWhitelistedUser: boolean,
        documentType: string,
        identityDocuments: IIdentityDocument[]
    ): Promise<any> {
        const registrationData = this.userManager.getUserInformation();
        const premiumExists = this.userManager.getPremiumExists();
        const customerExists = this.userManager.getCustomerExists();
        const normalizeOccupation = (occupation?: string): string | undefined => {
            if (!occupation) return undefined;
            const env = process.env.REACT_APP_ENV?.toLowerCase();
            const source = env === 'uat' || env === 'dev' ? UAT_OCCUPATIONS : PROD_OCCUPATIONS;

            const match = Object.values(source).find(
                (o) => o.EN.toLowerCase() === occupation.toLowerCase() || o.FR.toLowerCase() === occupation.toLowerCase()
            );
            return match ? match.EN : occupation;
        };

        let resolvedDocumentType = documentType.toUpperCase() as IDocumentType;
        const updateDocumentType = (docType: IDocumentType) => {
            this._setDocumentType?.(docType);
            this.documentManager.setDocumentType(docType);
        };
        if (isWhitelistedUser) {
            let selectedLabel: string;
            if (isWhitelistedUser && registrationData?.othersType && documentType === DOCUMENT_TYPES.OTHERS) {
                selectedLabel = registrationData.othersType;
            } else {
                selectedLabel = documentType;
            }
            if (selectedLabel && Array.isArray(identityDocuments)) {
                const match = identityDocuments.find((doc) => doc.en?.toLowerCase() === selectedLabel.toLowerCase()
                || doc.fr?.toLowerCase() === selectedLabel.toLowerCase()
                );
                resolvedDocumentType = match?.documentType.toUpperCase() as IDocumentType
                || (selectedLabel.toUpperCase() as IDocumentType);

                if (registrationData) {
                    registrationData.othersType = selectedLabel;
                }
                this._setDocumentType?.(resolvedDocumentType);
                this.documentManager.setDocumentType(resolvedDocumentType);
            } else {
                resolvedDocumentType = selectedLabel.toUpperCase() as IDocumentType;
                this._setDocumentType?.(resolvedDocumentType);
                this.documentManager.setDocumentType(resolvedDocumentType);
            }
        }
        updateDocumentType(resolvedDocumentType);
        const operationType = (customerExists && !premiumExists)
            ? OTP_OPERATION_TYPE.UPDATE_KYC
            : OTP_OPERATION_TYPE.REGISTRATION;
        const relativeUrl = isMetadatForm
            ? Constants.API_ENDPOINTS.CUSTOMER.META_ACCOUNT_CREATION
            : Constants.API_ENDPOINTS.CUSTOMER.ACCOUNT_CREATION;

        const submitRegistrationRequest: ISubmitRegistrationRequest = {
            relativeUrl,
            data: {
                msisdn: this.userManager.getMsisdn(),
                resource: {
                    language: ISOLanguageCodeEnum[i18next.language.toUpperCase() as keyof typeof ISOLanguageCodeEnum],
                    ...registrationData,
                    gender: String(this.dataManager.convertToGender(String(registrationData?.gender)))[0].toUpperCase(),
                    documentType: resolvedDocumentType,
                    occupation: normalizeOccupation(registrationData?.occupation),
                    operationType
                }
            }
        };

        return this.registrationService
            .requestAccountCreation(submitRegistrationRequest)
            .then(({ status }: ISubmitRegistrationResponseData) => {
                if (status === ResourceStatusEnum.SYSTEM_APPROVING || status === ResourceStatusEnum.NOT_VALIDATED) {
                    // Logger.log(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'Successfully requested account creation', status);

                    if (status === ResourceStatusEnum.NOT_VALIDATED) {
                        // Logger.log(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'Whitelisted user: accepting NOT_VALIDATED status');
                        this.setWhiteListedResponse(status);
                    }
                    return Promise.resolve();
                }

                Logger.error(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'Failed to request account creation on BE');
                return Promise.reject(ERROR_CODES.GENERIC_ERROR);
            }, (errorCode) => {
                Logger.error(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'Failed to request account creation', errorCode);
                this._requestSubmissionStatus = {
                    isCreated: false,
                    errorCode: Utils.convertToErrorCode(errorCode)
                };
                return Promise.reject(Utils.convertToErrorCode(errorCode));
            });
    }

    validateCustomerId(id: string): Promise<any> {
        return new Promise((resolve, reject) => {
            this.registrationService.validateCustomerId({ id })
                .then((response: IValidateCustomerIdResponseData) => {
                    const { status } = response;
                    if (status === ConsentOtpStatusEnum.VALID) {
                        // Logger.log(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'Successfully validated ID', response);
                        resolve(status);
                    } else {
                        Logger.error(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'Failed to validate ID: Invalid ID', response);
                        reject(ERROR_CODES.VALIDATE_CUSTOMER_ID.NOT_FOUND);
                    }
                })
                .catch((error) => {
                    Logger.error(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'Failed to validate ID', error);
                    reject(Utils.convertToErrorCode(error?.errorCode) || ERROR_CODES.GENERIC_ERROR);
                });
        });
    }

    validateReverseOtp({ otp, uniqueIdentifier }: IValidateReverseOtpRequest): Promise<any> {
        return new Promise((resolve, reject) => {
            this.registrationService.validateReverseOtp({ otp, uniqueIdentifier })
                .then(({ data }) => {
                    if (data?.status === InternalResourceStatusEnum.SUCCESS) {
                        Logger.log(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'Validated reverse otp', data);
                        resolve('');
                        return;
                    }
                    if (data?.status === InternalResourceStatusEnum.BLOCKED) {
                        Logger.error(LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER, 'User is blocked', data);
                        reject(ERROR_CODES.VALIDATE_REVERSE_OTP.BLOCKED);
                    }
                })
                .catch((error) => {
                    Logger.error(
                        LOGGER_TAGS.CUSTOMER_REGISTRATION_MANAGER,
                        'Failed to validate reverse OTP',
                        error?.message || error
                    );
                    reject(Utils.convertToErrorCode(error?.errorCode) || ERROR_CODES.GENERIC_ERROR);
                });
        });
    }

    getCustomerIRegistrationId(): string {
        return this._customerIRegistrationId;
    }

    setCustomerIRegistrationId(value: string) {
        this._customerIRegistrationId = value;
    }

    getWhiteListedResponse(): string {
        return this._whiteListedResponse;
    }

    setWhiteListedResponse(value: string): void {
        this._whiteListedResponse = value;
    }
}

export default new CustomerRegistrationManager();
